import React from "react";
import { toUTCFormat } from "@/lib/dateHelpers";
import { capitalize } from "lodash";
import Tag, { TagType, TagStyle } from "@/components/Tag/Tag";
import Tooltip from "@/components/Tooltip";
import Icon from "@/components/Icon";
import { Person, PlayingAbilityTestStatus } from "@/types";
import type { MaterialNames } from "@/components/material_symbol_names";
import { ADDITIONAL_STATUS_MAP, getConsolidatedPATStatusLabel, getPATSubmissionStatusTag } from "./pat-status";
import { buildPlayingAbilityTestPathAwareTooltip, isPatSubmissionStillMetForCurrentPath } from "./pathAwareExpiration";
import { isExcessiveScore } from "@/pages/events/[eventSlug]/(tabbed)/results/_utils";

export const PAT_EXEMPT_TOUR = [
  { value: "board_of_control_appeal", displayText: "Board of Control Appeal Decision Granted" },
  { value: "korn_ferry", displayText: "Korn Ferry" },
  { value: "lpga_tour", displayText: "LPGA Tour" },
  { value: "pga_of_australia", displayText: "PGA of Australia" },
  { value: "pga_of_canada", displayText: "PGA of Canada" },
  { value: "pga_of_germany", displayText: "PGA of Germany" },
  { value: "pga_of_great_britain", displayText: "PGA of Great Britain (British PGA)" },
  { value: "pga_of_new_zealand", displayText: "PGA of New Zealand" },
  { value: "pga_of_south_africa", displayText: "PGA of South Africa" },
  { value: "pga_tour", displayText: "PGA TOUR" },
  { value: "pga_tour_americas", displayText: "PGA TOUR Americas" },
  { value: "pga_tour_champions", displayText: "PGA TOUR Champions" }
] as const;

export type PATExemptTourValue = (typeof PAT_EXEMPT_TOUR)[number]["value"];

export const isPATExemptTourValue = (value: string): value is PATExemptTourValue =>
  PAT_EXEMPT_TOUR.some((tour) => tour.value === value);

export const PAT_TYPE_OPTIONS = [
  { value: "alternate_event", displayText: "Alternate PAT" },
  { value: "handicap_submission", displayText: "Handicap" },
  { value: "tour_exemption", displayText: "Exemption" },
  { value: "combined_events", displayText: "Combined Events" }
];

type PATStatusTag = {
  color: TagType;
  label: string;
  tagStyle?: TagStyle;
};

const RESULT_STATUS_MAP: Record<string, PATStatusTag> = {
  passed: { color: "success", label: getConsolidatedPATStatusLabel("passed") },
  qualified: { color: "information", label: getConsolidatedPATStatusLabel("qualifying"), tagStyle: "strong" },
  failed: { color: "error", label: getConsolidatedPATStatusLabel("failed") },
  expired: { color: "surface", label: getConsolidatedPATStatusLabel("expired") },
  registered: { color: "surface", label: "Registered", tagStyle: "strong" },
  pending: { color: "warn", label: "Pending", tagStyle: "strong" }
};

export const formatPatType = (type: string): string => {
  switch (type) {
    case "StandardPat":
    case "standard_pat":
      return "Standard PAT";
    case "AlternateEvent":
    case "alternate_event":
      return "Alternate PAT";
    case "CombinedEvents":
    case "combined_events":
      return "Combined Events";
    case "HandicapSubmission":
    case "handicap_submission":
      return "Handicap";
    case "TourExemption":
    case "tour_exemption":
      return "Exemption";
    case "CombinedScore":
    case "combined_score":
      return "Combined Score";
    default:
      return capitalize(type.replace(/([A-Z])/g, " $1").trim());
  }
};

const wrapWithPlainTooltip = (supportingText: string, children: React.ReactNode): React.ReactNode => (
  <Tooltip
    variant="plain"
    color="black"
    supportingText={supportingText}
    menuCorner="end-start"
    anchorCorner="start-start"
    positioning="fixed"
  >
    {children}
  </Tooltip>
);

export const createStatusElement = (
  result?: string | null,
  additionalStatus?: string | null,
  note?: string | null,
  submissionDate?: string | null,
  activeSuspension?: any,
  expirationTooltip?: string | null,
  richExpirationTooltip?: { title: string; supportingText: string } | null,
  suspensionTooltip?: string | null
): React.ReactNode => {
  if (result) {
    const normalized = result.toLowerCase();
    const mappedSubmissionStatus = getPATSubmissionStatusTag(result);
    const defaultData = { color: "outline" as TagType, label: mappedSubmissionStatus.label };
    const data = RESULT_STATUS_MAP[normalized] || defaultData;
    const showPlainTooltipIcon = !richExpirationTooltip && (!!expirationTooltip || !!suspensionTooltip);
    const tag = (
      <Tag
        tagType={data.color}
        tagStyle={data.tagStyle}
        {...(richExpirationTooltip && { tooltip: richExpirationTooltip })}
      >
        {showPlainTooltipIcon && <Icon name="error" size={16} className="mr-1" />}
        {data.label}
      </Tag>
    );
    if (expirationTooltip && !richExpirationTooltip) {
      return wrapWithPlainTooltip(expirationTooltip, tag);
    }
    if (suspensionTooltip && !richExpirationTooltip) {
      const tooltipText = note ? `${suspensionTooltip},\n${note}` : suspensionTooltip;
      return wrapWithPlainTooltip(tooltipText, tag);
    }
    return tag;
  }

  if (additionalStatus && ADDITIONAL_STATUS_MAP[additionalStatus]) {
    const label = ADDITIONAL_STATUS_MAP[additionalStatus];

    if (additionalStatus === "excused") {
      return (
        <Tooltip
          variant="plain"
          color="black"
          supportingText={note || "Excused"}
          menuCorner="end-start"
          anchorCorner="start-start"
          positioning="fixed"
        >
          <Tag tagType="information">
            <Icon name="error" size={20} className="mr-1" />
            {label}
          </Tag>
        </Tooltip>
      );
    }

    const suspensionDates = formatPATSuspensionTooltipText(activeSuspension, submissionDate);
    const tooltipText = note ? suspensionDates + ",\n" + note : suspensionDates;

    return wrapWithPlainTooltip(
      tooltipText,
      <div className="flex flex-row items-center">
        <Icon name="error" className="text-sys-brand-error mr-1" />
        {label}
      </div>
    );
  }

  return <span className="opacity-20">--</span>;
};

export const calculatePATProgramStatus = (
  pat: PlayingAbilityTestStatus
): "Complete" | "Incomplete" | "Suspended" | "Qualified" => {
  const suspended = !!pat.activeSuspension;
  if (suspended) return "Suspended";
  if (pat.expired) return "Incomplete";

  const status = pat.status?.toUpperCase();
  if (status === "QUALIFIED" || status === "QUALIFYING") return "Qualified";
  if (status === "PASSED") return "Complete";

  return pat.completed ? "Complete" : "Incomplete";
};

type PATSuspensionDates = {
  startDate?: string | null;
  endDate?: string | null;
} | null;

const formatPATSuspensionDateRange = (
  suspension?: PATSuspensionDates,
  fallbackStartDate?: string | null
): { startDateStr: string; endDateStr: string } => {
  const startDateStr = toUTCFormat(suspension?.startDate || fallbackStartDate || "") || "";
  const endDateStr = suspension?.endDate ? toUTCFormat(suspension.endDate) || "Indefinite" : "Indefinite";
  return { startDateStr, endDateStr };
};

export const formatPATSuspensionTooltipText = (
  suspension?: PATSuspensionDates,
  fallbackStartDate?: string | null
): string => {
  const { startDateStr, endDateStr } = formatPATSuspensionDateRange(suspension, fallbackStartDate);
  return `PAT Suspension ${startDateStr} - ${endDateStr}`;
};

export const formatPATSuspensionDetailsText = (
  suspension?: PATSuspensionDates,
  fallbackStartDate?: string | null
): string => {
  const { startDateStr, endDateStr } = formatPATSuspensionDateRange(suspension, fallbackStartDate);
  return `PAT Suspension: ${startDateStr} - ${endDateStr}`;
};

const isSuspensionAdditionalStatus = (additionalStatus?: string | null, result?: string | null): boolean =>
  !result && !!additionalStatus && additionalStatus !== "excused" && !!ADDITIONAL_STATUS_MAP[additionalStatus];

const causesPatSuspension = (
  additionalStatus?: string | null,
  result?: string | null,
  totalScore?: number | null
): boolean =>
  isSuspensionAdditionalStatus(additionalStatus, result) ||
  (result?.toLowerCase() === "failed" && isExcessiveScore(totalScore ?? null));

export const getPATSuspensionDetails = (pat: PlayingAbilityTestStatus): string | null => {
  const { activeSuspension } = pat || {};
  if (!activeSuspension) return null;

  return formatPATSuspensionTooltipText(activeSuspension);
};

export type FormatPATLineItemsOptions = {
  pathAwareExpiration?: boolean;
  person?: Person;
};

export const formatPATLineItems = (
  pat: PlayingAbilityTestStatus,
  options?: FormatPATLineItemsOptions
): Array<Record<string, any>> => {
  const pathAware = !!options?.pathAwareExpiration && !!options.person;
  const showPatSuspensionDetails = !!options?.pathAwareExpiration;

  return (
    pat?.submissions?.map((submission, index) => {
      const additionalStatus = (submission as any).additionalStatus as string | undefined;
      const note = (submission as any).note as string | undefined;
      const tour = (submission as any).tour as string | undefined;

      const handicap = (submission as any).handicap as number | undefined;

      let noteText = note || "";
      let expirationTooltip: string | null = null;
      let richExpirationTooltip: { title: string; supportingText: string } | null = null;
      let expirationAlertTooltip: { title: string; supportingText: string } | null = null;
      let expirationDetailsText: string | null = null;
      const rowSuspension = submission.suspension || pat?.activeSuspension || null;
      // Excessive-score suspension dates are attempt-scoped only; do not fall back to
      // pat.activeSuspension (that window may belong to a different attempt).
      const excessiveScoreFailed =
        submission.result?.toLowerCase() === "failed" && isExcessiveScore(submission.totalScore);
      const excessiveScoreSuspension = excessiveScoreFailed ? submission.suspension : null;
      const suspensionTooltip = excessiveScoreFailed
        ? formatPATSuspensionTooltipText(excessiveScoreSuspension, submission.date)
        : null;
      const suspensionDetailsText =
        showPatSuspensionDetails && causesPatSuspension(additionalStatus, submission.result, submission.totalScore)
          ? formatPATSuspensionDetailsText(
              excessiveScoreFailed ? excessiveScoreSuspension : rowSuspension,
              submission.date
            )
          : null;

      if (submission.expired === true && submission.expirationDate) {
        const expirationDateLabel = toUTCFormat(submission.expirationDate);
        const stillMet = pathAware ? isPatSubmissionStillMetForCurrentPath(options.person!, submission) : false;

        if (pathAware) {
          const tooltip =
            buildPlayingAbilityTestPathAwareTooltip({
              stillMet,
              expiredDate: submission.expirationDate,
              person: options.person
            }) ?? null;
          if (stillMet) {
            richExpirationTooltip = tooltip;
          } else {
            expirationAlertTooltip = tooltip;
            expirationDetailsText = `Expired: ${expirationDateLabel}`;
          }
        } else {
          expirationDetailsText = `Expired: ${expirationDateLabel}`;
          expirationTooltip = `PAT Expired ${expirationDateLabel}`;
        }
      }

      return {
        id: submission.id || `pat-submission-${index}`,
        patType: submission.patType,
        type: formatPatType(submission.patType),
        eventName: submission.name || null, // Event name from pat.event&.title
        date: toUTCFormat(submission.date),
        rawDate: submission.date,
        roundOneScore: submission.roundOneScore,
        roundTwoScore: submission.roundTwoScore,
        roundOneId: submission.roundOneId,
        roundTwoId: submission.roundTwoId,
        totalScore: submission.totalScore,
        targetScore: submission.targetScore,
        status: createStatusElement(
          submission.result,
          additionalStatus,
          note,
          submission.date,
          rowSuspension,
          expirationTooltip,
          richExpirationTooltip,
          suspensionTooltip
        ),
        details:
          additionalStatus === "no_show" && pat?.activeSuspension
            ? pat.activeSuspension.endDate
              ? `Ends ${toUTCFormat(pat.activeSuspension.endDate)}`
              : "Indefinite"
            : null,
        slug: (submission as any).slug,
        hasDocument: !!(submission as any).document,
        documentUrl: (submission as any).document?.url,
        note: noteText,
        handicap: handicap,
        tour: tour,
        result: submission.result,
        additionalStatus: additionalStatus || null,
        suspension: submission.suspension || null,
        expirationAlertTooltip,
        expirationDetailsText,
        suspensionDetailsText
      };
    }) || []
  );
};

export interface DocumentIconConfig {
  key?: string;
  name: MaterialNames;
  className: string;
  tooltip: {
    variant: "rich";
    color: "white";
    positioning: "fixed";
    anchorCorner: "end-start";
    menuCorner: "start-end";
    closeDelay: number;
    title?: string;
    supportingText: React.ReactNode;
  };
}

export const getPATExpirationAlertIconConfig = (
  tooltip?: { title: string; supportingText: string } | null
): DocumentIconConfig | null => {
  if (!tooltip) return null;

  return {
    key: "pat-expiration-alert",
    name: "error",
    className: "text-sys-brand-error",
    tooltip: {
      variant: "rich",
      color: "white",
      positioning: "fixed",
      anchorCorner: "end-start",
      menuCorner: "start-end",
      closeDelay: 500,
      title: tooltip.title,
      supportingText: tooltip.supportingText
    }
  };
};

/**
 * Creates the icon configuration for displaying a document icon with tooltip
 * in PAT-related tables. The tooltip shows the note and a link to view the document.
 *
 * @param hasDocument - Whether a document exists
 * @param documentUrl - URL to the document (optional)
 * @param note - The note text to display (optional)
 * @param formattedNote - Pre-formatted note text (optional, used for PATActivityTable)
 * @returns Icon configuration object or null if no document
 */
export const getPATDocumentIconConfig = (
  hasDocument: boolean,
  documentUrl?: string | null,
  note?: string | null,
  formattedNote?: string | null
): DocumentIconConfig | null => {
  if (!hasDocument) return null;

  const displayNote = formattedNote || note;

  return {
    name: "sticky_note_2",
    className: "text-sys-brand-on-surface-variant",
    tooltip: {
      variant: "rich",
      color: "white",
      positioning: "fixed",
      anchorCorner: "end-start",
      menuCorner: "start-end",
      closeDelay: 500,
      supportingText: (
        <div className="w-full text-left">
          <div className="text-label-large mb-2">Note:</div>
          {displayNote && <div className="mb-4 whitespace-pre-wrap">{displayNote}</div>}
          <div className="mb-2">Document attached.</div>
          {documentUrl && (
            <a
              href={documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sys-brand-primary"
              onClick={(e) => e.stopPropagation()}
            >
              View document
            </a>
          )}
        </div>
      )
    }
  };
};
